import { memo, useCallback, useEffect, useRef } from 'react'
import { useActiveSectionsTop, usePanelHistoryTop } from 'weblite-router'
import {
  Badge,
  BadgeProps,
  ButtonProps,
  Stack,
  styled,
  Typography,
} from '@mui/material'
import MuiButton from '@mui/material/Button'
import { CHAT_LIST_WRAPPER_ID } from '$comp/_app/components/leftPanel/components/chat-list'
import { useRoomListUnseenCount } from '$logic/room-seen/react-hook'
import {
  BackpackIcon,
  BackpackOutlinedIcon,
  ChatIcon,
  ChatOutlinedIcon,
  JaryanIcon,
  JaryanOutlinedIcon,
  ShoeprintOutlinedIcon,
  StationsIcon,
  StationsOutlinedIcon,
} from '$common/icons'
import { toLocale } from '$func/text'
import {
  switchToRoomList,
  switchToKooleh,
  switchToJaryan,
  switchToRahkar,
  switchToMavakeb,
} from '$logic/router'
import { isPhoneOrTablet } from '$func/environmentInfo'
import { nothing } from '$func/utils'
import { t } from '$i18n'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { stickToTopTransform } from '$setup/visual-viewport'

// Note: equals StyledIconButton.height + Stack.borderTop
export const NAVIGATION_BAR_HEIGHT = 0

interface Props {
  active?: boolean
}

const StyledButton = styled(MuiButton)<Props>`
  width: 20%;
  height: 64px;
  border-radius: 0;
  color: ${({ theme, active }) =>
    active ? theme.palette.neutral800 : theme.palette.neutral400};

  &.Mui-disabled {
    color: ${({ theme }) => theme.palette.neutral800};
  }

  display: flex;
  align-items: center;
  flex-direction: column;
  justify-content: center;
`

function NavigationButton(
  props: ButtonProps & {
    panel: string
    active?: boolean
    disabledWhileActive?: boolean
  },
) {
  const { onClick, panel, active, disabledWhileActive } = props
  return (
    <StyledButton
      {...props}
      disabled={active && disabledWhileActive}
      onClick={e => {
        onClick?.(e)
        analite('NAVIGATION_CLICK', { mode: panel })
      }}
    />
  )
}

NavigationButton.defaultProps = {
  disabledWhileActive: true,
  active: false,
}

const NavigationTitle = styled(Typography)`
  color: inherit;
  margin-top: 2px;
`

const MainActionIconWrapper = styled('div')`
  border-radius: 24px;
  max-width: 95%;
  max-height: 95%;
  height: 48px;
  width: 56px;
  background-color: ${({ theme }) => theme.palette.secondary500};
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledBadge = styled(Badge)<BadgeProps>({
  '& .MuiBadge-badge': {
    minWidth: 16,
    maxWidth: 22,
    height: 17,

    top: 4,
    right: 0,

    color: 'white',
    fontSize: '10px',
    padding: '1px 3px 0',
    border: '2px solid white',
    borderRadius: 9,
    pointerEvents: 'none',
  },
})

type PanelType = 'roomList' | 'rahkar' | 'jaryan' | 'kooleh' | 'mavakeb'

export function FakeChatIcon({ showBadge }: { showBadge: boolean }) {
  return (
    <NavigationButton panel="roomList" onClick={nothing} disabled>
      <StyledBadge invisible={!showBadge} color="success" badgeContent=" ">
        <ChatIcon />
      </StyledBadge>
      <NavigationTitle variant="subtitle3">
        {t('navigationBar.chat')}
      </NavigationTitle>
    </NavigationButton>
  )
}

export function Chat({
  panel,
  setShowBadge,
}: {
  panel?: string
  setShowBadge: (show: boolean) => void
}) {
  const { ALL: count } = useRoomListUnseenCount()

  const showBadge = !!count
  useEffect(() => {
    setShowBadge(showBadge)
  }, [showBadge, setShowBadge])

  return (
    <NavigationButton
      panel="roomList"
      onClick={() => {
        if (panel !== 'roomList') switchToRoomList()
        else {
          document
            .getElementById(CHAT_LIST_WRAPPER_ID)
            ?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
        }
      }}
      disabledWhileActive={false}
      active={panel === 'roomList'}
    >
      <StyledBadge
        badgeContent={count > 99 ? `${toLocale(99)}+` : toLocale(count)}
        color="success"
        invisible={!count}
      >
        {panel === 'roomList' ? <ChatIcon /> : <ChatOutlinedIcon />}
      </StyledBadge>
      <NavigationTitle variant="subtitle3">
        {t('navigationBar.chat')}
      </NavigationTitle>
    </NavigationButton>
  )
}

export const NavigationBar = memo(function NavigationBarComponent() {
  const isDesktop = useIsDesktopSize()
  const activeSection = useActiveSectionsTop()

  const roomListTop = usePanelHistoryTop('roomList')?.location
  const homeState = usePanelHistoryTop('home')?.state
  const homeActiveTab = homeState?.tab
  const showBadgeRef = useRef(false)
  const setShowBadge = useCallback((badgeRefValue: boolean) => {
    showBadgeRef.current = badgeRefValue
  }, [])

  const panel: PanelType | undefined = [
    'roomList',
    'jaryan',
    'kooleh',
    'mavakeb',
    'rahkar',
  ].includes(homeActiveTab)
    ? (homeActiveTab as PanelType)
    : undefined

  const isHidden = isDesktop
    ? !panel || roomListTop === 'roomList/search' || homeActiveTab === 'rahkar'
    : !panel ||
      activeSection === 'mid' ||
      roomListTop === 'roomList/search' ||
      homeActiveTab === 'rahkar'

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      sx={{
        borderTop: '1px solid #f1f1f1',
        padding: '0 0',
        position: 'absolute',
        bottom: 0,
        width: 1,
        zIndex: isHidden ? -1 : 1,
        backgroundColor: 'white',
        // Navigation bar position is absolute and based on it's parent which is VisibilityHandler. So its transform must be like VisibikityHandler which is stickToTop.
        transform: stickToTopTransform,

        // TODO: Keep this till implementing desktop layout
        ...(!isPhoneOrTablet
          ? {
              maxWidth: '400px',
              left: 0,
              right: 0,
              margin: 'auto',
            }
          : null),
      }}
    >
      <NavigationButton
        onClick={switchToKooleh}
        panel="kooleh"
        active={panel === 'kooleh'}
      >
        {panel === 'kooleh' ? <BackpackIcon /> : <BackpackOutlinedIcon />}
        <NavigationTitle variant="subtitle3">
          {t('navigationBar.backpack')}
        </NavigationTitle>
      </NavigationButton>

      <NavigationButton
        onClick={switchToJaryan}
        panel="jaryan"
        active={panel === 'jaryan'}
      >
        {panel === 'jaryan' ? <JaryanIcon /> : <JaryanOutlinedIcon />}
        <NavigationTitle variant="subtitle3">
          {t('navigationBar.movement')}
        </NavigationTitle>
      </NavigationButton>

      <NavigationButton panel="rahkar" onClick={switchToRahkar}>
        <MainActionIconWrapper>
          <ShoeprintOutlinedIcon sx={{ color: 'neutral800' }} />
        </MainActionIconWrapper>
      </NavigationButton>

      <NavigationButton
        onClick={switchToMavakeb}
        panel="mavakeb"
        active={panel === 'mavakeb'}
      >
        {panel === 'mavakeb' ? <StationsIcon /> : <StationsOutlinedIcon />}
        <NavigationTitle variant="subtitle3">
          {t('navigationBar.stations')}
        </NavigationTitle>
      </NavigationButton>

      {isHidden ? (
        <FakeChatIcon showBadge={showBadgeRef.current} />
      ) : (
        <Chat setShowBadge={setShowBadge} panel={panel} />
      )}
    </Stack>
  )
})
